import { NumberInput as MantineNumberInput } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { type DebouncedFunc } from "lodash";
import { useCallback } from "react";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { quoteRequestLineQuery } from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import { MaterialSelect } from "#modules/quoteRequestLines/MaterialSelect";
import { QuoteRequestLineFormData } from "#modules/quoteRequestLines/types/QuoteRequestLineFormData";
import { selectInputOnFocus } from "#shared/helpers/selectInputOnFocus";
import { Group } from "#shared/view/components/Group";
import classes from "#shared/view/designSystem/inputs/InputStyles.module.css";
import { TextButton } from "#shared/view/designSystem/TextButton";
import { AssemblyWithChildren, JobStatusType, MaterialGradeType } from "shared";

import { useCustomInputs } from "../useCustomInputs";
import { useShouldShowCustomInputs } from "../useShouldShowCustomInputs";
import { INPUT_MIN_WIDTH } from "./constants";
import { CustomInputsModal } from "./CustomInputsModal";
import { SurfaceTreatmentSelect } from "./SurfaceTreatmentSelect";

type Props = {
  onQuantityChange: DebouncedFunc<(quantity: number | undefined) => void>;
  onMaterialChange: (data: {
    quoteRequestLineId: string;
    predictionStatus: JobStatusType | null;
    payload: { materialName: string; materialGrade: MaterialGradeType | null };
  }) => void;
  isPredicting: boolean;
  assembliesInBatch: AssemblyWithChildren<QuoteRequestLineDto>[];
  quoteRequestLine: QuoteRequestLineDto;
  isChild?: boolean;
};

export const PredictionData = ({
  onQuantityChange,
  isPredicting,
  onMaterialChange,
  assembliesInBatch,
  quoteRequestLine,
  isChild = false,
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { control } = useFormContext<QuoteRequestLineFormData>();
  const [areCustomInputsVisible, { open: openCustomInputsModal, close: closeCustomInputModal }] =
    useDisclosure(false);

  const partType = useWatch({ control, name: "part.type" });
  const shouldShowCustomInputs = useShouldShowCustomInputs(partType);

  const { customInputs } = useCustomInputs({ enabled: shouldShowCustomInputs });

  const quoteRequestLineId = useWatch({ control, name: "quoteRequestLineId" });
  const { field: quantityField } = useController({
    name: isChild ? "part.quantityInAssembly" : "quantity",
    control,
  });
  const isBatch = assembliesInBatch.length > 1;
  const persistedQuantity = isChild
    ? (quoteRequestLine.part.quantityInAssembly ?? 1)
    : quoteRequestLine.quantity;

  const currentAssembly = assembliesInBatch.find(
    (assembly) => assembly.quoteRequestLine.id === quoteRequestLineId,
  );

  const _onMaterialChange = (
    selectedMaterialName: string,
    selectedMaterialGrade: MaterialGradeType | null,
  ) => {
    if (!quoteRequestLineId) {
      return;
    }

    onMaterialChange({
      quoteRequestLineId,
      predictionStatus: currentAssembly?.quoteRequestLine.predictionStatus ?? null,
      payload: {
        materialName: selectedMaterialName,
        materialGrade: selectedMaterialGrade,
      },
    });
  };

  const onSubmitCustomInputs = useCallback(() => {
    void queryClient.invalidateQueries(quoteRequestLineQuery({ id: quoteRequestLineId }));
  }, [quoteRequestLineId, queryClient]);

  if (!quoteRequestLineId) {
    return null;
  }

  return (
    <Group align="flex-end" justify="flex-start" gap={"xs"} onClick={(e) => e.stopPropagation()}>
      {!isBatch && (
        <MantineNumberInput
          label={t("qrl.quantity")}
          disabled={isPredicting}
          maw={INPUT_MIN_WIDTH}
          miw={INPUT_MIN_WIDTH}
          classNames={{
            input: classes.input,
          }}
          hideControls
          value={quantityField.value ?? undefined}
          defaultValue={quantityField.value ?? undefined}
          onFocus={selectInputOnFocus}
          onValueChange={(value) => {
            quantityField.onChange(value.floatValue);
            if (!value.floatValue) {
              onQuantityChange.cancel();

              return;
            }
            if (value.floatValue === persistedQuantity) {
              onQuantityChange.cancel();

              return;
            }
            onQuantityChange(value.floatValue);
          }}
        />
      )}

      <MaterialSelect
        onChange={_onMaterialChange}
        disabled={isPredicting}
        assembly={currentAssembly}
        quoteRequestLine={quoteRequestLine}
      />

      <SurfaceTreatmentSelect
        quoteRequestLineIds={[quoteRequestLineId]}
        disabled={isPredicting}
        label={t("qrl.surfaceTreatment")}
      />

      {shouldShowCustomInputs && customInputs.length > 0 && (
        <TextButton onClick={openCustomInputsModal}>affiner le chiffrage...</TextButton>
      )}

      <CustomInputsModal
        customInputs={customInputs}
        isOpen={areCustomInputsVisible}
        isPredicting={isPredicting}
        onClose={closeCustomInputModal}
        quoteRequestLineId={quoteRequestLineId}
        onSubmit={onSubmitCustomInputs}
      />
    </Group>
  );
};
